// ═══════════════════════════════════════════════════════════════════════════
// PesacoreIHEngine.js — Kernel Root Super-Admin
// Orchestrateur système : état global, commandes, déploiements, audit
// Totalement découplé de UniversalLearningEngine et PesaEngine
// ═══════════════════════════════════════════════════════════════════════════

const getPesacoreStore = () =>
  typeof window !== 'undefined' ? window.__PESACORE_IH_STORE__ : null;

// ─── Constantes système ───────────────────────────────────────────────────

export const VALID_SERVICES = [
  'auth', 'content', 'pesas', 'analytics', 'notifications', 'storage',
  'sim-engine', 'db-proxy', 'cache-layer', 'worker-jobs', 'api-core'
];

export const VALID_ROLES = [
  'superadmin', 'admin', 'administrateur', 'entreprise',
  'collectivité', 'établissement', 'pro', 'learner', 'particulier'
];

export const PLAN_QUOTAS = {
  freemium:   { maxParcours: 2,        maxLearners: 30,   maxStorage: '500MB' },
  academia:   { maxParcours: Infinity, maxLearners: 500,  maxStorage: '10GB'  },
  pro:        { maxParcours: Infinity, maxLearners: 1000, maxStorage: '50GB'  },
  entreprise: { maxParcours: Infinity, maxLearners: Infinity, maxStorage: '500GB' },
};

export const HEALTH_THRESHOLDS = {
  cpu:    { warn: 70, critical: 80,  penalty: { warn: 10, critical: 20  } },
  memory: { warn: 75, critical: 85,  penalty: { warn: 15, critical: 25  } },
  disk:   { warn: 85, critical: 92,  penalty: { warn: 10, critical: 20  } },
  queue:  { warn: 20, critical: 50,  penalty: { warn: 5,  critical: 15  } },
};

// ─── Sous-système 1 : SystemStateManager ─────────────────────────────────

class SystemStateManager {
  constructor(initialMetrics = {}) {
    this.metrics = {
      cpu:        initialMetrics.cpu        ?? 0,
      memory:     initialMetrics.memory     ?? 0,
      disk:       initialMetrics.disk       ?? 0,
      network:    initialMetrics.network    ?? 0,
      queueDepth: initialMetrics.queueDepth ?? 0,
    };
    this.services  = initialMetrics.services  || [];
    this.snapshots = [];
  }

  updateMetrics(incoming) {
    Object.assign(this.metrics, incoming);
    // Garder un historique glissant de 60 points (1 min à 1s/point)
    this.snapshots.push({ ...this.metrics, ts: Date.now() });
    if (this.snapshots.length > 60) this.snapshots.shift();
  }

  updateServices(services) {
    this.services = services;
  }

  computeHealthScore() {
    let score  = 100;
    const issues = [];

    for (const [metric, thresholds] of Object.entries(HEALTH_THRESHOLDS)) {
      const value = this.metrics[metric] ?? 0;
      if (value >= thresholds.critical) {
        score -= thresholds.penalty.critical;
        issues.push({ metric, level: 'critical', value });
      } else if (value >= thresholds.warn) {
        score -= thresholds.penalty.warn;
        issues.push({ metric, level: 'warn', value });
      }
    }

    // Services dégradés ou en erreur
    const degraded = this.services.filter(s => s.status === 'degraded').length;
    const errored  = this.services.filter(s => s.status === 'error').length;
    score -= degraded * 5;
    score -= errored  * 15;
    if (errored > 0) issues.push({ metric: 'services', level: 'critical', value: errored });

    return {
      score:  Math.max(0, Math.min(100, Math.round(score))),
      status: score >= 80 ? 'healthy' : score >= 55 ? 'warning' : 'critical',
      issues,
    };
  }

  getMetricHistory(metric, points = 30) {
    return this.snapshots
      .slice(-points)
      .map(s => ({ ts: s.ts, value: s[metric] ?? 0 }));
  }

  serialize() {
    return { metrics: { ...this.metrics }, services: [...this.services] };
  }
}

// ─── Sous-système 2 : RulesValidator ─────────────────────────────────────

class RulesValidator {
  // ── Learner ────────────────────────────────────────────────────────────
  validateCreateLearner(data) {
    const errors = [];
    if (!data.firstName?.trim()) errors.push('firstName obligatoire');
    if (!data.lastName?.trim())  errors.push('lastName obligatoire');
    if (!data.email?.trim())     errors.push('email obligatoire');
    if (!data.cohorteId)         errors.push('cohorteId obligatoire');
    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.push('email invalide');
    }
    return { valid: errors.length === 0, errors };
  }

  // ── Cohorte ────────────────────────────────────────────────────────────
  validateDeleteCohorte(cohorte, learnerCount) {
    if (cohorte.status === 'active' && learnerCount > 0) {
      return {
        valid: false,
        errors: [
          `Impossible de supprimer une cohorte active avec ${learnerCount} apprenant(s).`,
          'Transférez ou archivez d\'abord les apprenants.',
        ],
        canForce: true, // super-admin peut forcer
      };
    }
    return { valid: true, errors: [], canForce: false };
  }

  // ── Staff ──────────────────────────────────────────────────────────────
  validateCreateStaff(data) {
    const errors = [];
    if (!data.nom?.trim())   errors.push('nom obligatoire');
    if (!data.email?.trim()) errors.push('email obligatoire');
    if (!data.role?.trim())  errors.push('rôle obligatoire');
    if (data.role && !VALID_ROLES.includes(data.role)) {
      errors.push(`rôle invalide : ${data.role}. Valides : ${VALID_ROLES.join(', ')}`);
    }
    return { valid: errors.length === 0, errors };
  }

  // ── Plan / Quotas ──────────────────────────────────────────────────────
  validateQuota(plan, resource, currentCount) {
    const quotas = PLAN_QUOTAS[plan];
    if (!quotas) return { valid: false, errors: [`Plan inconnu : ${plan}`] };

    const limit = quotas[resource];
    if (limit === undefined) return { valid: true, errors: [] };
    if (limit === Infinity)  return { valid: true, errors: [] };

    if (currentCount >= limit) {
      return {
        valid: false,
        errors: [`Quota atteint pour ${resource} (${currentCount}/${limit}) sur le plan ${plan}`],
        limit,
        current: currentCount,
      };
    }
    return { valid: true, errors: [], remaining: limit - currentCount };
  }

  // ── Service restart ────────────────────────────────────────────────────
  validateServiceRestart(serviceId) {
    if (!VALID_SERVICES.includes(serviceId)) {
      return { valid: false, errors: [`Service inconnu : "${serviceId}". Valides : ${VALID_SERVICES.join(', ')}`] };
    }
    return { valid: true, errors: [] };
  }

  // ── Migration / Déploiement ────────────────────────────────────────────
  validateMigration(migration) {
    const errors = [];
    if (!migration.name?.trim())   errors.push('Nom de migration obligatoire');
    if (!migration.target?.trim()) errors.push('Cible (target) obligatoire');
    if (!migration.script?.trim()) errors.push('Script de migration obligatoire');
    if (migration.isDestructive && !migration.confirmationToken) {
      errors.push('Migration destructive : confirmationToken requis');
    }
    return { valid: errors.length === 0, errors };
  }

  // ── Instance (tenant) ─────────────────────────────────────────────────
  validateInstanceOperation(instance, operation) {
    const errors = [];
    if (!instance?.tenantId) errors.push('tenantId manquant');

    if (operation === 'delete') {
      if (instance.totalLearners > 0) {
        errors.push(`Instance contient ${instance.totalLearners} apprenant(s) — suppression bloquée`);
      }
      if (instance.subscription?.status === 'active') {
        errors.push('Abonnement actif — désactivez d\'abord l\'abonnement');
      }
    }

    if (operation === 'suspend') {
      if (instance.plan === 'entreprise' && !instance.suspensionApprovedBy) {
        errors.push('Suspension d\'un compte entreprise requiert une approbation explicite');
      }
    }

    return { valid: errors.length === 0, errors };
  }
}

// ─── Sous-système 3 : CommandPipeline ────────────────────────────────────

class CommandPipeline {
  constructor(validator) {
    this.validator = validator;
    this.queue     = [];       // commandes en attente
    this.history   = [];       // commandes exécutées
    this.dryRunMode = false;
  }

  /**
   * Enregistre une commande avec validation préalable.
   * Retourne { valid, errors, commandId } AVANT exécution.
   * En dryRunMode, la commande n'est jamais exécutée.
   */
  prepare(type, payload, meta = {}) {
    const commandId = `cmd_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const validation = this._validate(type, payload);

    const command = {
      id:          commandId,
      type,
      payload,
      meta:        { ...meta, preparedAt: new Date().toISOString() },
      validation,
      status:      validation.valid ? 'ready' : 'rejected',
      dryRun:      this.dryRunMode,
      executedAt:  null,
      result:      null,
    };

    if (!validation.valid) {
      this._archiveToHistory(command, 'rejected');
      return { valid: false, errors: validation.errors, commandId };
    }

    if (this.dryRunMode) {
      command.status = 'dry-run';
      this._archiveToHistory(command, 'dry-run');
      return { valid: true, errors: [], commandId, dryRun: true };
    }

    this.queue.push(command);
    return { valid: true, errors: [], commandId };
  }

  /**
   * Exécute la prochaine commande valide de la queue.
   * L'appelant fournit un handler async.
   */
  async execute(commandId, handler) {
    const idx = this.queue.findIndex(c => c.id === commandId);
    if (idx === -1) {
      return { success: false, error: `Commande ${commandId} introuvable dans la queue` };
    }

    const command = this.queue[idx];
    this.queue.splice(idx, 1);

    try {
      const result = await handler(command.payload);
      command.status     = 'executed';
      command.executedAt = new Date().toISOString();
      command.result     = result;
      this._archiveToHistory(command, 'executed');
      return { success: true, result, commandId };
    } catch (err) {
      command.status = 'failed';
      command.result = { error: err.message };
      this._archiveToHistory(command, 'failed');
      return { success: false, error: err.message, commandId };
    }
  }

  toggleDryRun() {
    this.dryRunMode = !this.dryRunMode;
    return this.dryRunMode;
  }

  clearQueue() {
    const cleared = this.queue.length;
    this.queue = [];
    return cleared;
  }

  _validate(type, payload) {
    switch (type) {
      case 'CREATE_LEARNER':      return this.validator.validateCreateLearner(payload);
      case 'DELETE_COHORTE':      return this.validator.validateDeleteCohorte(payload.cohorte, payload.learnerCount);
      case 'CREATE_STAFF':        return this.validator.validateCreateStaff(payload);
      case 'RESTART_SERVICE':     return this.validator.validateServiceRestart(payload.serviceId);
      case 'RUN_MIGRATION':       return this.validator.validateMigration(payload);
      case 'INSTANCE_OPERATION':  return this.validator.validateInstanceOperation(payload.instance, payload.operation);
      case 'CHECK_QUOTA':         return this.validator.validateQuota(payload.plan, payload.resource, payload.currentCount);
      default:
        // Commandes inconnues : on laisse passer avec warning
        return { valid: true, errors: [], warning: `Type de commande non vérifié : ${type}` };
    }
  }

  _archiveToHistory(command, finalStatus) {
    this.history.unshift({ ...command, finalStatus });
    if (this.history.length > 200) this.history = this.history.slice(0, 200);
  }

  getHistory(limit = 50) {
    return this.history.slice(0, limit);
  }

  getPendingQueue() {
    return [...this.queue];
  }
}

// ─── Sous-système 4 : DeploymentOrchestrator ─────────────────────────────

class DeploymentOrchestrator {
  constructor() {
    this.migrations    = [];   // migrations enregistrées
    this.deployments   = [];   // déploiements lancés
    this.rollbackStack = [];   // états pour rollback
  }

  registerMigration(migration) {
    const id = `mig_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const entry = {
      id,
      ...migration,
      status:       'pending',
      registeredAt: new Date().toISOString(),
      executedAt:   null,
      rolledBackAt: null,
    };
    this.migrations.push(entry);
    return id;
  }

  async deploy(migrationId, executor) {
    const mig = this.migrations.find(m => m.id === migrationId);
    if (!mig) return { success: false, error: `Migration ${migrationId} introuvable` };
    if (mig.status === 'executed') return { success: false, error: 'Migration déjà exécutée' };

    // Snapshot pré-déploiement pour rollback
    this.rollbackStack.push({
      migrationId,
      snapshotAt: new Date().toISOString(),
      snapshot:   mig.rollbackSnapshot || null,
    });

    try {
      mig.status = 'running';
      const result = await executor(mig);
      mig.status     = 'executed';
      mig.executedAt = new Date().toISOString();
      mig.result     = result;

      this.deployments.push({
        migrationId,
        deployedAt: mig.executedAt,
        result,
      });

      return { success: true, migrationId, result };
    } catch (err) {
      mig.status = 'failed';
      mig.error  = err.message;
      return { success: false, error: err.message, migrationId };
    }
  }

  async rollback(migrationId, rollbackExecutor) {
    const entry = this.rollbackStack.find(r => r.migrationId === migrationId);
    if (!entry) return { success: false, error: 'Aucun snapshot de rollback disponible' };

    const mig = this.migrations.find(m => m.id === migrationId);
    try {
      await rollbackExecutor(entry.snapshot);
      if (mig) {
        mig.status       = 'rolled-back';
        mig.rolledBackAt = new Date().toISOString();
      }
      // Retirer du rollbackStack
      this.rollbackStack = this.rollbackStack.filter(r => r.migrationId !== migrationId);
      return { success: true, migrationId };
    } catch (err) {
      return { success: false, error: err.message, migrationId };
    }
  }

  getPendingMigrations() {
    return this.migrations.filter(m => m.status === 'pending');
  }

  getDeploymentHistory(limit = 20) {
    return this.deployments.slice(-limit).reverse();
  }
}

// ─── Sous-système 5 : AuditLogger ────────────────────────────────────────

class AuditLogger {
  constructor() {
    this.entries = [];
    this.sessionId = `session_${Date.now()}`;
  }

  /**
   * Enregistre une action root horodatée.
   * @param {string} action   — identifiant de l'action (ex: 'RESTART_SERVICE')
   * @param {object} payload  — données associées
   * @param {object} actor    — { userId, email, role }
   * @param {string} outcome  — 'success' | 'failure' | 'dry-run' | 'rejected'
   */
  log(action, payload, actor = {}, outcome = 'success', notes = '') {
    const entry = {
      id:        `audit_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      sessionId: this.sessionId,
      action,
      payload:   this._sanitize(payload),
      actor:     { userId: actor.userId || 'unknown', email: actor.email || '', role: actor.role || '' },
      outcome,
      notes,
      timestamp: new Date().toISOString(),
    };

    this.entries.unshift(entry);
    // Limite à 500 entrées en mémoire
    if (this.entries.length > 500) this.entries = this.entries.slice(0, 500);

    return entry;
  }

  getEntries(filters = {}, limit = 100) {
    let result = [...this.entries];

    if (filters.action)  result = result.filter(e => e.action === filters.action);
    if (filters.outcome) result = result.filter(e => e.outcome === filters.outcome);
    if (filters.actor)   result = result.filter(e => e.actor.userId === filters.actor);
    if (filters.since)   result = result.filter(e => e.timestamp >= filters.since);

    return result.slice(0, limit);
  }

  getSummary() {
    const total    = this.entries.length;
    const failures = this.entries.filter(e => e.outcome === 'failure').length;
    const rejected = this.entries.filter(e => e.outcome === 'rejected').length;
    const dryRuns  = this.entries.filter(e => e.outcome === 'dry-run').length;

    const actionCounts = this.entries.reduce((acc, e) => {
      acc[e.action] = (acc[e.action] || 0) + 1;
      return acc;
    }, {});

    return { total, failures, rejected, dryRuns, actionCounts, sessionId: this.sessionId };
  }

  // Masque les champs sensibles avant stockage
  _sanitize(payload) {
    if (!payload || typeof payload !== 'object') return payload;
    const sensitive = ['password', 'token', 'secret', 'apiKey', 'confirmationToken'];
    const clean = { ...payload };
    sensitive.forEach(k => { if (clean[k]) clean[k] = '[REDACTED]'; });
    return clean;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// PesacoreIHEngine — Orchestrateur principal
// ═══════════════════════════════════════════════════════════════════════════

export class PesacoreIHEngine {
  constructor(config = {}) {
    this.version    = '1.0.0';
    this.engineId   = `pesa_ih_${Date.now()}`;
    this.actor      = config.actor || { userId: 'unknown', email: '', role: 'superadmin' };

    // Sous-systèmes
    this.systemState  = new SystemStateManager(config.initialMetrics || {});
    this.validator    = new RulesValidator();
    this.pipeline     = new CommandPipeline(this.validator);
    this.deployer     = new DeploymentOrchestrator();
    this.audit        = new AuditLogger();

    // État global du kernel
    this.state = {
      kernelStatus:       'initializing',  // initializing | ready | degraded | locked
      dryRunMode:         false,
      globalStats:        config.globalStats || {},
      instances:          config.instances   || [],
      pendingChanges:     0,
      lastHealthCheck:    null,
      lastFullSync:       null,
      initializationTime: new Date().toISOString(),
    };

    this._log('KERNEL_BOOT', { version: this.version, engineId: this.engineId }, 'success');
    console.log(`[PesacoreIHEngine] Kernel boot — id: ${this.engineId}`);
  }

  // ── API publique : validation instantanée (sans exécution) ──────────────

  /**
   * Point d'entrée principal : valide une action root AVANT envoi au backend.
   * Retourne { valid, errors, warnings, commandId? }
   */
  validateAndPrepare(actionType, payload) {
    const result = this.pipeline.prepare(actionType, payload, { actor: this.actor });

    this._log(
      `PREPARE_${actionType}`,
      payload,
      result.valid ? 'success' : 'rejected',
      result.errors?.join(' | ') || ''
    );

    this._notifyStore({ pendingChanges: this.pipeline.queue.length });

    return result;
  }

  /**
   * Exécute une commande préparée.
   * @param {string} commandId  — id retourné par validateAndPrepare
   * @param {Function} handler  — async (payload) => result
   */
  async executeCommand(commandId, handler) {
    const result = await this.pipeline.execute(commandId, handler);

    this._log(
      'EXECUTE_COMMAND',
      { commandId, success: result.success },
      result.success ? 'success' : 'failure',
      result.error || ''
    );

    this._notifyStore({ pendingChanges: this.pipeline.queue.length });

    return result;
  }

  // ── Monitoring ───────────────────────────────────────────────────────────

  updateSystemMetrics(metrics) {
    this.systemState.updateMetrics(metrics);
    const health = this.systemState.computeHealthScore();

    if (health.status !== this.state.kernelStatus &&
        ['degraded', 'critical'].includes(health.status)) {
      this.state.kernelStatus = health.status === 'critical' ? 'degraded' : 'ready';
      this._log('KERNEL_STATUS_CHANGE', { health }, 'success');
    }

    this._notifyStore({ systemHealth: health });
    return health;
  }

  updateServices(services) {
    this.systemState.updateServices(services);
  }

  runHealthCheck() {
    const health = this.systemState.computeHealthScore();
    this.state.lastHealthCheck = new Date().toISOString();
    this._log('HEALTH_CHECK', health, health.status === 'healthy' ? 'success' : 'failure');
    this._notifyStore({ lastHealthCheck: this.state.lastHealthCheck, systemHealth: health });
    return health;
  }

  // ── Dry-run mode ─────────────────────────────────────────────────────────

  toggleDryRun() {
    this.state.dryRunMode = this.pipeline.toggleDryRun();
    this._log('DRY_RUN_TOGGLE', { active: this.state.dryRunMode }, 'success');
    this._notifyStore({ dryRunMode: this.state.dryRunMode });
    return this.state.dryRunMode;
  }

  // ── Déploiements ─────────────────────────────────────────────────────────

  registerMigration(migration) {
    const validation = this.validator.validateMigration(migration);
    if (!validation.valid) {
      this._log('REGISTER_MIGRATION', migration, 'rejected', validation.errors.join(' | '));
      return { success: false, errors: validation.errors };
    }

    const id = this.deployer.registerMigration(migration);
    this.state.pendingChanges++;
    this._log('REGISTER_MIGRATION', { ...migration, id }, 'success');
    this._notifyStore({ pendingChanges: this.state.pendingChanges });
    return { success: true, migrationId: id };
  }

  async deployMigration(migrationId, executor) {
    const result = await this.deployer.deploy(migrationId, executor);
    if (result.success) this.state.pendingChanges = Math.max(0, this.state.pendingChanges - 1);
    this._log('DEPLOY_MIGRATION', { migrationId, success: result.success }, result.success ? 'success' : 'failure', result.error || '');
    this._notifyStore({ pendingChanges: this.state.pendingChanges });
    return result;
  }

  async rollbackMigration(migrationId, rollbackExecutor) {
    const result = await this.deployer.rollback(migrationId, rollbackExecutor);
    this._log('ROLLBACK_MIGRATION', { migrationId }, result.success ? 'success' : 'failure', result.error || '');
    return result;
  }

  // ── Instances ────────────────────────────────────────────────────────────

  validateInstanceOp(instance, operation) {
    const result = this.validator.validateInstanceOperation(instance, operation);
    this._log(
      `INSTANCE_${operation.toUpperCase()}`,
      { tenantId: instance?.tenantId, operation },
      result.valid ? 'success' : 'rejected',
      result.errors?.join(' | ') || ''
    );
    return result;
  }

  // ── Quota check rapide ───────────────────────────────────────────────────

  checkQuota(plan, resource, currentCount) {
    return this.validator.validateQuota(plan, resource, currentCount);
  }

  // NOUVELLE SECTION : USER ACTIVITY (signaux utilisateurs)

  /**
   * Signal reçu quand un utilisateur s'inscrit ou se connecte
   * @param {Object} activity - { type: 'register'|'login', userId, tenantId, timestamp, ip, userAgent }
   */
  onUserActivity(activity) {
    // Validation basique
    if (!activity || !activity.type || !activity.userId) {
      this._log('USER_ACTIVITY_INVALID', { activity }, 'failure', 'Missing required fields');
      return { success: false, error: 'Invalid activity data' };
    }

    // 1. Log dans l'audit (consultable plus tard)
    this._log(
      `USER_${activity.type.toUpperCase()}`,
      { 
        userId: activity.userId, 
        tenantId: activity.tenantId,
        userAgent: activity.userAgent 
      },
      'success',
      activity.ip ? `IP: ${activity.ip}` : ''
    );

    // 2. Mettre à jour les stats globales si inscription
    if (activity.type === 'register') {
      this.state.globalStats.totalUsers = (this.state.globalStats.totalUsers || 0) + 1;
      this.state.pendingChanges++;
      
      this._notifyStore({ 
        totalUsers: this.state.globalStats.totalUsers,
        pendingChanges: this.state.pendingChanges 
      });
    }

    // 3. Vérifier les seuils (alerte si activité anormale)
    this._checkUserThresholds(activity);

    // 4. Notifier le store pour affichage temps-réel dans l'UI
    this._notifyStore({
      lastUserActivity: {
        ...activity,
        processedAt: new Date().toISOString()
      }
    });

    return { success: true };
  }

  /**
   * Vérifie des seuils d'activité pour déclencher des alertes
   * @private
   */
  _checkUserThresholds(activity) {
    const total = this.state.globalStats.totalUsers || 0;
    
    // Seuil d'alerte : plus de 1000 inscriptions (à paramétrer)
    if (total > 1000 && activity.type === 'register') {
      this._notifyStore({
        systemAlerts: [{
          id: `alert_${Date.now()}`,
          severity: 'warning',
          title: 'Forte activité inscriptions',
          message: `${total} inscriptions depuis le démarrage du kernel`,
          timestamp: new Date().toISOString()
        }]
      });
    }
  }

  // ── Audit ────────────────────────────────────────────────────────────────

  getAuditLog(filters, limit) {
    return this.audit.getEntries(filters, limit);
  }

  getAuditSummary() {
    return this.audit.getSummary();
  }

  // ── Getters ──────────────────────────────────────────────────────────────

  getKernelStatus() {
    return {
      status:       this.state.kernelStatus,
      dryRunMode:   this.state.dryRunMode,
      pendingChanges: this.state.pendingChanges,
      health:       this.systemState.computeHealthScore(),
      engineId:     this.engineId,
      version:      this.version,
      uptime:       Date.now() - new Date(this.state.initializationTime).getTime(),
      queueDepth:   this.pipeline.queue.length,
      pendingMigrations: this.deployer.getPendingMigrations().length,
      lastHealthCheck: this.state.lastHealthCheck,
    };
  }

  getMetricHistory(metric, points = 30) {
    return this.systemState.getMetricHistory(metric, points);
  }

  getCommandHistory(limit = 50) {
    return this.pipeline.getHistory(limit);
  }

  getDeploymentHistory(limit = 20) {
    return this.deployer.getDeploymentHistory(limit);
  }

  getPendingMigrations() {
    return this.deployer.getPendingMigrations();
  }

  // ── Sérialisation ────────────────────────────────────────────────────────

  serialize() {
    return {
      engineId:    this.engineId,
      version:     this.version,
      state:       { ...this.state },
      systemState: this.systemState.serialize(),
      auditSummary: this.audit.getSummary(),
      commandHistory: this.pipeline.getHistory(20),
      deploymentHistory: this.deployer.getDeploymentHistory(10),
      totalUsers: this.state.globalStats.totalUsers,
    };
  }

  // ── Utilitaires internes ─────────────────────────────────────────────────

  _log(action, payload, outcome = 'success', notes = '') {
    return this.audit.log(action, payload, this.actor, outcome, notes);
  }

  _notifyStore(updates = {}) {
    const store = getPesacoreStore();
    if (!store) return;
    store.setState(updates);
  }

  destroy() {
    this._log('KERNEL_SHUTDOWN', { engineId: this.engineId }, 'success');
    console.log(`[PesacoreIHEngine] Kernel shutdown — id: ${this.engineId}`);
  }
}